import React, { useMemo } from "react";
import { ProductDetailsModal } from "../../../../../../interfaces/ProductObject";
import { DineInRoundItem } from "../../../../../../interfaces/DineInObject";
import { pendingDineInPlateCount } from "../../../../../../services/helpers/DineInSummaryHelper";
import VariantLineKeyHelper from "../../../../../../services/helpers/VariantLineKeyHelper";
import { pluralize } from "../../../../../../services/helpers/PluralizeHelper";
import { formatInr } from "../../../../../../services/helpers/MoneyHelper";
import BillDialog from "../../../../../shared/Bill/BillDialog";
import BillLines from "../../../../../shared/Bill/BillLines";
import { buildBillLines } from "./dineInDetailBuilder";

interface DineInBillReviewModalProps {
  tableNumber: number;
  /** Placed items only — the caller has already excluded draft rounds. */
  placedItems: DineInRoundItem[];
  products: ProductDetailsModal[];
  onClose: () => void;
  onGenerate: () => void;
}

/**
 * A last look before the money is called.
 *
 * Read-only on purpose: every correction available on this screen — accepting,
 * rejecting — lives on the ticket behind it, where the item sits in the
 * context of its round. Offering edits here as well would mean two places to
 * change the same thing and two chances for them to disagree.
 */
const DineInBillReviewModal: React.FC<DineInBillReviewModalProps> = ({
  tableNumber,
  placedItems,
  products,
  onClose,
  onGenerate,
}) => {
  const { lines, total, rejectedCount } = useMemo(
    () => buildBillLines(placedItems, products),
    [placedItems, products]
  );

  // Same rule the server enforces (10020): no bill while an item still waits
  // on a kitchen decision. Counts plates, not rows.
  const waitingCount = useMemo(() => pendingDineInPlateCount(placedItems), [placedItems]);

  return (
    <BillDialog ariaLabel={`Bill for table ${tableNumber}`} onClose={onClose} showClose>
      <h3 className="bill__title">Bill · Table {tableNumber}</h3>
      <p className="bill__sub">Check the items before generating</p>

      {lines.length === 0 ? (
        <p className="bill__empty">Nothing has been ordered at this table yet.</p>
      ) : (
        <BillLines
          lines={lines.map((line) => ({
            key: VariantLineKeyHelper.toLineKey(line.productId, line.variantId),
            name: line.title || "Menu item",
            quantity: line.quantity,
            amount: line.lineTotal,
          }))}
        />
      )}

      {rejectedCount > 0 && (
        <p className="bill__note">{pluralize(rejectedCount, "item")} rejected — not charged.</p>
      )}

      {waitingCount > 0 && (
        <p className="bill__note">{pluralize(waitingCount, "item")} waiting for a decision.</p>
      )}

      <div className="bill__total">
        <span>Total</span>
        <span className="bill__total-value">{formatInr(total)}</span>
      </div>

      <div className="bill__actions">
        <button type="button" className="bill__btn" onClick={onClose}>
          Back
        </button>
        <button
          type="button"
          className="bill__btn bill__btn--primary"
          onClick={onGenerate}
          disabled={lines.length === 0 || waitingCount > 0}
        >
          Generate bill
        </button>
      </div>
    </BillDialog>
  );
};

export default DineInBillReviewModal;
